<template>
  <BasePopupModal
    v-model:is-modal-open="localOpened"
    size="medium"
    :title="$t('global.dossier')"
  >
    <template #footer>
      <div class="mt-1 flex h-full gap-x-1">
        <BaseButton
          :title="$t('button.choose')"
          icon="paper-plane"
          size="small"
          :disabled="!choosenDossier || isSubmitting"
          @click="handleChooseDossier"
        />
      </div>
    </template>
    <template #body>
      <div class="flex h-[60vh] w-full flex-col">
        <div
          v-if="loading"
          class="flex shrink-0 flex-row items-center justify-center"
        >
          <LoadingComponent />
        </div>
        <div v-else class="flex min-h-0 flex-1 flex-col gap-2">
          <template
            v-if="
              !(
                choosenDossier &&
                invoice?.invoiceable_type === 'App\\Models\\Person'
              )
            "
          >
            <div class="flex shrink-0 flex-row">
              <div class="flex-1">&nbsp;</div>
              <div class="shrink-0">
                <BaseSearchInput v-model="searchText" />
              </div>
            </div>
            <div class="min-h-0 flex-1 overflow-y-auto">
              <div class="grid grid-cols-2 gap-3">
                <button
                  v-for="dossier in dossiers"
                  :key="dossier.id"
                  class="rounded border p-2 text-left"
                  :class="[
                    choosenDossier === dossier.id
                      ? 'border-primary shadow-md'
                      : ''
                  ]"
                  @click="handleSelectedDossier(dossier)"
                >
                  <span class="flex flex-col">
                    <span class="flex shrink-0 flex-row items-center">
                      <span class="line-clamp-1 flex-1 font-semibold">
                        {{ dossier.concernedPerson?.full_name }}
                      </span>
                      <span class="shrink-0">
                        <span
                          class="bg-primary text-contrast-primary rounded px-1 py-0.5 text-xs"
                          :data-tint="dossier.funeral_home?.color"
                        >
                          {{ dossier.funeral_home?.initials }}
                        </span>
                      </span>
                    </span>
                    <span class="text-xs font-medium">
                      # {{ dossier.reference }}
                    </span>
                    <span>
                      <span
                        :data-tint="
                          dossier_types.find((type) => type.id === dossier.type)
                            ?.color
                        "
                        class="bg-primary text-contrast-primary rounded px-1 py-0.5 text-xs font-medium"
                      >
                        {{
                          dossier_types.find((type) => type.id === dossier.type)
                            ?.name
                        }}
                      </span>
                    </span>
                    <span v-if="$slots['dossier-extra']" class="mt-2">
                      <slot name="dossier-extra" :dossier="dossier" />
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </template>
          <template v-else-if="choosenDossier">
            <div class="flex shrink-0 flex-row">
              <div class="flex-1">&nbsp;</div>
              <div class="shrink-0">
                <BaseButton
                  color="dark"
                  icon="arrow-left"
                  :title="$t('global.back_to_dossier_list')"
                  @click="choosenDossier = null"
                />
              </div>
            </div>
            <div
              v-if="dossier_model"
              class="mt-6 min-h-0 flex-1 overflow-y-auto"
            >
              <div class="flex flex-col items-center space-y-4">
                <div
                  class="w-1/2 rounded border border-slate-300 p-2 pt-1.5 text-left shadow-md dark:border dark:border-white dark:bg-slate-700"
                >
                  <div class="flex shrink-0 flex-row items-start">
                    <div class="flex flex-1 flex-col">
                      <h2 class="text-base font-medium">
                        <span class="uppercase">
                          {{ $t('global.dossier') }} :
                          {{ dossier_model.reference }}
                        </span>
                      </h2>
                      <h2 class="text-base font-medium">
                        <span class="uppercase">
                          {{
                            $t(
                              dossier_model.concernedPerson.is_deceased
                                ? 'global.deceased'
                                : 'global.beneficiary'
                            )
                          }}
                          : {{ dossier_model.concernedPerson?.full_name }}
                        </span>
                      </h2>
                      <h2 class="text-base font-medium">
                        <span class="uppercase">
                          {{ $t('global.funeralHome') }} :
                          {{ dossier_model.funeral_home.name }}
                        </span>
                      </h2>
                    </div>
                  </div>
                </div>
                <div
                  v-if="invoice?.invoiceable_type === 'App\\Models\\Person'"
                  class="w-1/2 text-left"
                >
                  <InfoPanel
                    :description="
                      $t('global.would_you_inject_items_doc_in_dossier')
                    "
                    type="warning"
                    background-colored
                    class="text-medium py-7 text-gray-800"
                  >
                    <template #default>
                      <WalkmanButton
                        v-model="invoice.inject_item"
                        :options="[
                          { value: true, label: $t('enums.yesNo.1') },
                          { value: false, label: $t('enums.yesNo.0') }
                        ]"
                        selected-background="text-blue-300 bg-gray-100"
                        size="small"
                      />
                    </template>
                  </InfoPanel>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </BasePopupModal>
</template>

<script>
import LoadingComponent from '@c/TheLoadingComponent.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BasePopupModal from '@c/addf-package/components/modal/BasePopupModal.vue'
import { mapActions, mapGetters } from 'vuex'
import BaseSearchInput from '@c/addf-package/components/BaseInput/BaseSearchInput.vue'
import { debounce } from '@u/debounce'
import InfoPanel from '@ui/InfoPanel.vue'
import WalkmanButton from '@c/WalkmanButton.vue'

export default {
  name: 'SearchDossierModal',
  components: {
    WalkmanButton,
    InfoPanel,
    BaseSearchInput,
    BasePopupModal,
    BaseButton,
    LoadingComponent
  },
  props: {
    opened: {
      type: Boolean,
      required: true
    }
  },
  emits: ['update:opened', 'selected-dossier'],
  data() {
    return {
      dossiers: [],
      loading: false,
      choosenDossier: null,
      dossier_model: null,
      searchText: '',
      isSubmitting: false
    }
  },
  computed: {
    ...mapGetters({
      invoice: 'invoicing/getInvoice',
      dossier_types: 'metadata/getMetadataDossierTypes'
    }),
    localOpened: {
      get() {
        return this.opened
      },
      set(value) {
        this.$emit('update:opened', value)
      }
    }
  },
  watch: {
    opened(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue) {
          this.loadDossier()
        }
      }
    },
    searchText() {
      this.debouncedSearch(this.searchText)
    }
  },
  created() {
    this.debouncedSearch = debounce((term) => {
      if (term && term.length >= 3) {
        this.loadDossier()
      } else if (!term || term.length === 0) {
        this.loadDossier()
      }
    }, 1000)
  },
  methods: {
    ...mapActions({
      processFetchDossierListAction: 'dossier/fetchDossierList'
    }),
    loadDossier() {
      this.loading = true
      this.processFetchDossierListAction({
        term: this.searchText
      })
        .then((response) => (this.dossiers = response.dossiers))
        .finally((_) => {
          this.loading = false
        })
    },
    handleSelectedDossier(dossier) {
      this.dossier_model = dossier
      this.choosenDossier = dossier.id
      this.invoice.inject_item = false
    },
    handleChooseDossier() {
      if (this.isSubmitting) return

      this.isSubmitting = true

      this.$emit('selected-dossier', this.choosenDossier)

      this.$nextTick(() => {
        this.isSubmitting = false
      })
    }
  }
}
</script>
